import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { TypingText } from "@workspace/ui/components/typing-text";
import { cn } from "@workspace/ui/lib/utils";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import { useEffect } from "react";

type BrandHeaderProps = {
  onHeaderTypingComplete?: () => void;
  showBears?: boolean;
  skipAnimation?: boolean;
  isLandingPage?: boolean;
};

function BearIcon({ show }: { show: boolean }) {
  return (
    <motion.div
      animate={{ opacity: show ? 1 : 0, scale: show ? 1 : 0.9 }}
      initial={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <video
        autoPlay
        className="size-20"
        loop
        muted
        onCanPlay={(e) => {
          e.currentTarget.playbackRate = 2;
        }}
        playsInline
        preload={show ? "auto" : "metadata"}
        src="/bear_alpha_smooth.webm"
      />
    </motion.div>
  );
}

export function BrandHeader({
  onHeaderTypingComplete,
  showBears = true,
  skipAnimation = false,
  isLandingPage = false,
}: BrandHeaderProps) {
  useEffect(() => {
    if (skipAnimation) {
      onHeaderTypingComplete?.();
    }
  }, [skipAnimation, onHeaderTypingComplete]);

  const titleContent = skipAnimation ? (
    <motion.span
      animate={{
        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      }}
      className="inline-block font-display text-6xl font-bold tracking-wide text-transparent"
      style={{
        backgroundImage: "var(--selca-text-gradient)",
        backgroundSize: "200% 100%",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
      }}
      transition={{
        backgroundPosition: {
          duration: 8,
          ease: "easeInOut",
          repeat: Number.POSITIVE_INFINITY,
        },
      }}
    >
      selca
    </motion.span>
  ) : (
    <TypingText
      animate={{
        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
      }}
      className="inline-block font-display text-6xl font-bold tracking-wide text-transparent"
      cursor="|"
      onComplete={onHeaderTypingComplete}
      showCursor={!showBears}
      speed={60}
      startOnView={false}
      style={{
        backgroundImage: "var(--selca-text-gradient)",
        backgroundSize: "200% 100%",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
      }}
      text="selca"
      transition={{
        backgroundPosition: {
          duration: 8,
          ease: "easeInOut",
          repeat: Number.POSITIVE_INFINITY,
        },
      }}
    />
  );

  return (
    <div className="mb-10 flex items-center justify-center gap-4">
      {/* <BearIcon show={skipAnimation || showBears} /> */}

      <div className="relative flex flex-col items-center">
        <div className="flex w-[6.5rem] justify-center">{titleContent}</div>

        <motion.div
          animate={{
            opacity: skipAnimation || showBears ? 0.7 : 0,
            y: skipAnimation || showBears ? 0 : -6,
          }}
          className={cn(
            "absolute left-1/2 top-full mt-1 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap text-sm",
            isLandingPage ? "landing-text-secondary" : "text-muted-foreground"
          )}
          initial={
            skipAnimation ? { opacity: 0.7, y: 0 } : { opacity: 0, y: -6 }
          }
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <span>셀카 — "selfie"</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label="What does selca mean?"
                className={cn(
                  "flex size-4 items-center justify-center rounded-full transition-colors",
                  isLandingPage
                    ? "hover:landing-text-primary"
                    : "hover:text-foreground"
                )}
                type="button"
              >
                <Info className="size-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs" side="bottom">
              <p>"Selca" is Korean for "selfie," short for "self-camera."</p>
            </TooltipContent>
          </Tooltip>
        </motion.div>
      </div>

      {/* <BearIcon show={skipAnimation || showBears} /> */}
    </div>
  );
}
